import classNames from 'classnames'
import { match } from 'ts-pattern'
import { pluralize } from '../../utils/text-utils.ts'
import { HiddenByScopeNote } from './hidden-by-scope-note.tsx'
import type { Categorization } from './resolution-categorization.ts'
import { buildResolutionFunnelRows, type FunnelRow, type FunnelVariant } from './resolution-funnel-rows.ts'
import * as styles from './resolution-funnel.css.ts'
import {
  areFiltersEqual,
  getAnalyzedTotal,
  toPercentage,
  type ResolutionFilter,
  type ResolutionPanelData,
} from './resolution-taxonomy.ts'

/** A stage with no findings still shows its bar, dimmed, so the funnel keeps its shape. */
const EMPTY_FILL_OPACITY = 0.25

export type ResolutionFunnelProps = {
  data: ResolutionPanelData
  variant?: FunnelVariant
  activeFilter?: ResolutionFilter
  onFilterChange?: (filter: ResolutionFilter | undefined) => unknown
  /** Findings removed from the data set by the active data scope, footnoted beside the total. */
  hiddenByScopeCount?: number
  /** Recolours the leaf rows to the buckets the categorization groups them into. */
  categorization?: Categorization
  /** The scan's analysis was cancelled, which the not-yet-analyzed callout says when nothing runs. */
  isAnalysisCancelled?: boolean
}

/**
 * The expanded state of the resolution panel: a nested breakdown of how findings flow
 * through Pixee, where every level subdivides the one above it.
 */
export const ResolutionFunnel: React.FC<ResolutionFunnelProps> = ({
  data,
  variant = 'all-findings',
  activeFilter,
  onFilterChange,
  hiddenByScopeCount = 0,
  categorization,
  isAnalysisCancelled = false,
}) => {
  // Until a finding finishes analysis there is no resolution to break down, whichever quick view
  // is selected: a first batch still running would otherwise draw a funnel of zeros.
  if (variant === 'unanalyzed' || getAnalyzedTotal(data.resolution) === 0) {
    return (
      <UnanalyzedCallout
        count={data.unanalyzedCount + data.inProgressCount}
        isAnalysisCancelled={isAnalysisCancelled && data.inProgressCount === 0}
      />
    )
  }

  const rows = buildResolutionFunnelRows({ data, variant, categorization })
  const rootCount = rows[0]?.count ?? 0

  return (
    <div className={styles.container}>
      <div className={styles.headerRow}>
        <span className={styles.headerLabel}>Stage</span>
        <span className={styles.headerLabel}>
          Share of total ({rootCount})
          <HiddenByScopeNote count={hiddenByScopeCount} />
        </span>
        <span className={styles.headerLabelRight}>Count</span>
        <span className={styles.headerLabelRight}>% of parent</span>
      </div>
      <div className={styles.rows}>
        {rows.map(row => (
          <FunnelRowItem
            key={row.key}
            row={row}
            rootCount={rootCount}
            activeFilter={activeFilter}
            onFilterChange={onFilterChange}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * Stands in for the funnel in the Unanalyzed quick view, which holds no analyzed findings and so has
 * no resolution to break down. Counts in-progress findings with the untouched ones, as the collapsed
 * bar's empty state does: neither has a resolution yet.
 */
const UnanalyzedCallout: React.FC<{ count: number; isAnalysisCancelled: boolean }> = ({
  count,
  isAnalysisCancelled,
}) => (
  <div className={styles.unanalyzedCallout}>
    <div className={styles.unanalyzedCalloutHeadline}>
      <span className={styles.unanalyzedCalloutCount}>{count}</span>
      <span className={styles.unanalyzedCalloutLabel}>{pluralize(count, 'finding', 'findings')} not yet analyzed</span>
    </div>
    <span className={styles.unanalyzedCalloutHint}>
      {isAnalysisCancelled
        ? 'Analysis was cancelled. Resolution and severity numbers populate once analysis runs.'
        : 'Resolution and severity numbers populate as analyses complete.'}
    </span>
  </div>
)

const FunnelRowItem: React.FC<{
  row: FunnelRow
  rootCount: number
  activeFilter: ResolutionFilter | undefined
  onFilterChange?: (filter: ResolutionFilter | undefined) => unknown
}> = ({ row, rootCount, activeFilter, onFilterChange }) => {
  const isRoot = row.depth === 0
  // Requires an active filter before any row can match. `areFiltersEqual` is a true equality, so
  // it answers yes for two absent filters -- correct in itself, and wrong here: every aggregate
  // row carries no filter, so at rest All findings, Unanalyzed, In progress, Analyzed and Pixee
  // can fix all read as active and wear the rail with nothing filtered.
  const isActive = !!activeFilter && areFiltersEqual(activeFilter, row.filter)
  const isDimmed = !!activeFilter && !!row.filter && !isActive
  const isClickable = (!!row.filter || !!row.clearsFilter) && !!onFilterChange

  const sharePercentage = toPercentage({ count: row.count, total: rootCount })
  const parentPercentage = isRoot
    ? 100
    : Math.round(toPercentage({ count: row.count, total: row.parentCount }) * 10) / 10

  // Toggling off an already-active filter returns the table to the unfiltered view, and so does the
  // root row, whose set is the whole quick view.
  const onClick = () => onFilterChange?.(isActive || row.clearsFilter ? undefined : row.filter)

  const content = (
    <>
      <span className={styles.label}>
        {Array.from({ length: row.depth }).map((_, level) => (
          <span
            key={level}
            className={classNames(
              styles.indent,
              match({ isDeepest: level === row.depth - 1, branch: row.branch })
                // Only the deepest guide draws the elbow into this row; an "in" branch also stops
                // its vertical rule there, since it is the last row at that level.
                .with({ isDeepest: true, branch: 'in' }, () => styles.indentVariant.last)
                .with({ isDeepest: true }, () => styles.indentVariant.mid)
                .otherwise(() => styles.indentVariant.line)
            )}
          />
        ))}
        <span className={isRoot ? styles.rootDot : styles.dot} style={{ backgroundColor: row.color }} />
        <span className={isRoot ? styles.rootName : styles.name}>{row.label}</span>
        <span className={styles.description}>{row.description}</span>
      </span>
      <span className={isRoot ? styles.rootTrack : styles.track}>
        <span
          className={row.isInProgress ? styles.fillInProgress : styles.fill}
          style={{
            width: `${sharePercentage}%`,
            backgroundColor: row.color,
            opacity: row.count === 0 ? EMPTY_FILL_OPACITY : 1,
          }}
        />
      </span>
      <span className={classNames(styles.count, styles.countByDepth[row.depth])}>{row.count}</span>
      <span className={styles.percentage}>
        <span className={styles.percentageValue({ isRoot: isRoot || undefined })}>
          {parentPercentage}
          <span className={styles.percentageUnit}>%</span>
        </span>
      </span>
    </>
  )

  const className = styles.row({
    isClickable: isClickable || undefined,
    isActive: isActive || undefined,
    isDimmed: isDimmed || undefined,
    isRoot: isRoot || undefined,
  })

  if (!isClickable) {
    return <div className={className}>{content}</div>
  }

  return (
    <button type="button" className={className} onClick={onClick} aria-pressed={isActive}>
      {content}
    </button>
  )
}
